import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import React, { useEffect, useState } from "react";
import { Button, ShowProps, useDataProvider } from "react-admin";
import IconPlus from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { GridLabel, GridinRow, GridinRowsWithSpace } from "utils/style";
import { GetParam, StudentResources } from "utils/tools";
import { find } from "lodash";
import moment from "moment";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
    padding: "1rem",
  },
  summaryButton: {
    position: "relative",
    top: 20,
    padding: "8px 16px",
  },
  academicYearLabel: {
    marginTop: "5px",
  },
  MuiAccordionSummary: {
    "& .MuiAccordionSummary-content": {
      margin: "15px 0px 5px",
    },
  },
});

const RegularStudentScheduleTable = (props: ShowProps) => {
  const classes = useStyles();
  const provider = useDataProvider();
  const param = GetParam();

  const [academicYear, setAcademicYear] = useState<IAcademicYear[] | null>(
    null,
  );
  const [academicYearId, setAcademicYearId] = useState<number>();
  const [academicTerms, setAcademicTerms] = useState<IAcademicTerm[]>([]);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [studentSchedules, setStudentSchedules] = useState<IStudentSchedule[]>(
    [],
  );

  const [loading, setLoading] = useState<boolean>(false);

  const format = "DD / MM / YYYY";

  const { id } = props;
  const studentResources = StudentResources(Number(id));

  useEffect(() => {
    provider
      .getList("academicYears", {
        filter: {
          SchoolId: Number(studentResources.schoolId),
        },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setAcademicYear(data as IAcademicYear[]);

        // The default is active AcademicYearId
        // If there is not active AcademicYearId,
        // I assume the default is  latest AcademicYearId
        const activeAcademicYear = data.find(
          (item) => item.status.toLowerCase() === "active",
        );

        if (activeAcademicYear) {
          setAcademicYearId(activeAcademicYear.id as number);
        } else {
          if (data.length > 0) {
            setAcademicYearId(data[data.length - 1].id as number);
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (academicYear) {
      provider
        .getList("academicTerms", {
          filter: { AcademicYearId: Number(academicYearId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setAcademicTerms(data as IAcademicTerm[]);
        });

      provider
        .getList("areas", {
          filter: { SchoolId: Number(studentResources.schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setAreas(data as IArea[]);
        });

      setLoading(true);
      provider
        .getList("studentSchedules", {
          filter: {
            StudentId: Number(id),
            AcademicYearId: Number(academicYearId),
          },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "StartedAt",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setStudentSchedules(data as IStudentSchedule[]);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearId]);

  const handleAcademicYearChange = (event: any) => {
    setAcademicYearId(event.target.value);
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="regular-student-schedule-content"
        id="regular-student-schedule-header"
        className={classes.MuiAccordionSummary}
      >
        <h3 className={classes.academicYearLabel}>Regular Student Schedules</h3>
      </AccordionSummary>
      <AccordionDetails>
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12} style={GridinRowsWithSpace}>
            <div>
              <FormControl variant="filled" className="form-control">
                <InputLabel id="academicYear">Regular Academic Year</InputLabel>
                {academicYearId && (
                  <Select
                    labelId="academicYear"
                    id="academicYear"
                    value={academicYearId}
                    onChange={handleAcademicYearChange}
                  >
                    {academicYear.map((value: any) => {
                      return (
                        <MenuItem value={value.id} key={value.id}>
                          {value.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </div>

            <div>
              <Button
                onClick={() =>
                  props.history.push(
                    `/studentSchedules/create?source=${JSON.stringify({
                      studentId: Number(id),
                      schoolId: Number(studentResources.schoolId),
                      parentId: Number(studentResources.parent?.id),
                    })}&studentId=${Number(id)}${
                      param.get("parentId")
                        ? `&parentId=${param.get("parentId")}`
                        : ""
                    }`,
                  )
                }
                label="Update Regular Schedule"
              >
                <IconPlus />
              </Button>
            </div>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Term</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Days of Week</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentSchedules.length > 0 ? (
                    <>
                      {studentSchedules.map(
                        (studentSchedule: IStudentSchedule, key: number) => {
                          return (
                            <TableRow key={`schedule-${key}`}>
                              <TableCell>
                                {(() => {
                                  const term = find(academicTerms, [
                                    "id",
                                    studentSchedule.termId,
                                  ]);

                                  if (term === undefined) {
                                    return;
                                  }

                                  return term.name;
                                })()}
                              </TableCell>
                              <TableCell>{`${moment
                                .utc(studentSchedule.startDate)
                                .format(format)} - ${moment
                                .utc(studentSchedule.endDate)
                                .format(format)}`}</TableCell>
                              <TableCell>
                                <SanitizedGrid container spacing={2}>
                                  {studentSchedule.detail === null ? (
                                    <>
                                      <Grid item xs={12}>
                                        <Typography variant="body2">
                                          ---
                                        </Typography>
                                      </Grid>
                                    </>
                                  ) : (
                                    <>
                                      <Grid item xs={12} style={GridinRow}>
                                        <Typography
                                          variant="body2"
                                          style={GridLabel}
                                        >
                                          Block No. :{" "}
                                          {studentSchedule.detail.blockNumber}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} style={GridinRow}>
                                        <Typography
                                          variant="body2"
                                          style={GridLabel}
                                        >
                                          Street :{" "}
                                          {studentSchedule.detail.street}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} style={GridinRow}>
                                        <Typography
                                          variant="body2"
                                          style={GridLabel}
                                        >
                                          Postal Code :{" "}
                                          {studentSchedule.detail.postalCode}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={12} style={GridinRow}>
                                        <Typography
                                          variant="body2"
                                          style={GridLabel}
                                        >
                                          Area :{" "}
                                          {(() => {
                                            const area = find(areas, [
                                              "id",
                                              studentSchedule.areaId,
                                            ]);

                                            if (
                                              area === undefined ||
                                              area === null
                                            ) {
                                              return "-";
                                            }

                                            return area.name;
                                          })()}
                                        </Typography>
                                      </Grid>
                                    </>
                                  )}
                                </SanitizedGrid>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  if (studentSchedule.detail === null) {
                                    return "---";
                                  }

                                  if (studentSchedule.isTaking === false) {
                                    return "----";
                                  }

                                  if (
                                    studentSchedule.detail.isFromSchool &&
                                    studentSchedule.detail.isToSchool
                                  ) {
                                    return "Both";
                                  } else if (
                                    studentSchedule.detail.isToSchool
                                  ) {
                                    return "AM";
                                  } else if (
                                    studentSchedule.detail.isFromSchool
                                  ) {
                                    return "PM";
                                  }
                                })()}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  if (studentSchedule.detail === null) {
                                    return "---";
                                  }

                                  if (studentSchedule.isTaking === false) {
                                    return "----";
                                  }

                                  let days: string = "";

                                  if (studentSchedule.detail.isMonday) {
                                    days = days + " Monday,";
                                  }
                                  if (studentSchedule.detail.isTuesday) {
                                    days = days + " Tuesday,";
                                  }
                                  if (studentSchedule.detail.isWednesday) {
                                    days = days + " Wednesday,";
                                  }
                                  if (studentSchedule.detail.isThursday) {
                                    days = days + " Thursday,";
                                  }
                                  if (studentSchedule.detail.isFriday) {
                                    days = days + " Friday,";
                                  }

                                  return days.replace(/,(?=[^,]*$)/, "");
                                })()}
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        {loading ? "Loading..." : "No results found."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </SanitizedGrid>
      </AccordionDetails>
    </Accordion>
  );
};

export default RegularStudentScheduleTable;
